/** Persist which CFA level the visitor is preparing for — and track intent. */

import { phiTrack } from './phiTrack';
import { recordFunnelEvent } from './siteStats';

const STORAGE_KEY = 'cfa-preferred-level';
const VALID = new Set(['l1', 'l2', 'l3']);

export function normalizePreferredLevel(value) {
  const raw = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^level[-_\s]*/, '')
    .replace(/^l/, 'l');
  if (raw === '1' || raw === 'l1') return 'l1';
  if (raw === '2' || raw === 'l2') return 'l2';
  if (raw === '3' || raw === 'l3') return 'l3';
  return null;
}

export function getPreferredLevel(fallback = 'l1') {
  try {
    const stored = normalizePreferredLevel(window.localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;
  } catch {
    /* ignore */
  }
  return fallback;
}

export function setPreferredLevel(level) {
  const next = normalizePreferredLevel(level);
  if (!next || !VALID.has(next)) return getPreferredLevel();
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent('cfa-preferred-level', { detail: { level: next } }));
  } catch {
    /* ignore */
  }
  return next;
}

/**
 * Infer CFA level from a URL path (hubs, quizzes, blogs).
 * Returns null when the path is level-agnostic (e.g. /pricing, /blog index).
 */
export function inferLevelFromPath(pathname = '') {
  const path = String(pathname || '').split('?')[0].toLowerCase();
  if (path.startsWith('/level/3') || path.startsWith('/quiz/l3')) return 'l3';
  if (path.startsWith('/level/2') || path.startsWith('/quiz/l2')) return 'l2';
  if (
    path.startsWith('/quiz/session') ||
    path === '/quiz/review' ||
    path.startsWith('/study-guide') ||
    path.startsWith('/glossary')
  ) {
    return 'l1';
  }
  const blog = path.match(/^\/blog\/([a-z0-9-]+)/);
  if (blog) return inferLevelFromBlogSlug(blog[1]);
  return null;
}

/** Infer level from a blog slug (or explicit post.level). */
export function inferLevelFromBlogSlug(slug = '') {
  const s = String(slug || '').toLowerCase();
  if (!s) return null;
  if (s.includes('level-3') || s.includes('level_3') || s.startsWith('cfa-l3')) return 'l3';
  if (s.includes('level-2') || s.includes('level_2') || s.startsWith('cfa-l2')) return 'l2';
  if (s.includes('level-1') || s.includes('level_1') || s.includes('free-cfa')) return 'l1';
  if (s.includes('vs-level-3')) return 'l3';
  if (s.includes('vs-level-2')) return 'l2';
  return null;
}

export function blogPostLevel(post) {
  if (!post) return null;
  const explicit = normalizePreferredLevel(post.level);
  if (explicit) return explicit;
  return inferLevelFromBlogSlug(post.slug);
}

/**
 * Remember + analytics for level intent.
 * @param {string} level
 * @param {{ source?: string, path?: string, slug?: string, persist?: boolean }} meta
 */
export function trackLevelIntent(level, meta = {}) {
  const next = normalizePreferredLevel(level);
  if (!next) return null;
  const persist = meta.persist !== false;
  const previous = getPreferredLevel(null);
  if (persist) setPreferredLevel(next);

  const props = {
    level: next,
    source: String(meta.source || 'unknown').slice(0, 40),
    path: String(meta.path || (typeof window !== 'undefined' ? window.location.pathname : '')).slice(
      0,
      160
    ),
    slug: meta.slug ? String(meta.slug).slice(0, 80) : undefined,
    previous: previous || undefined,
    changed: previous ? previous !== next : true,
  };

  phiTrack('level_intent', props);
  phiTrack(`level_intent_${next}`, props);
  recordFunnelEvent(`level_intent__${next}`).catch(() => {});
  if (meta.source) {
    recordFunnelEvent(`level_intent__${next}__${String(meta.source).slice(0, 24)}`).catch(() => {});
  }
  return next;
}

/** Resolve level for the current page and track a view (does not force-overwrite preference on ambiguous pages). */
export function trackLevelPageView(pathname, extra = {}) {
  const inferred = inferLevelFromPath(pathname);
  if (!inferred) {
    phiTrack('page_view', {
      path: String(pathname || '').slice(0, 160),
      preferredLevel: getPreferredLevel('l1'),
      ...extra,
    });
    return null;
  }
  // Persist when the URL clearly signals a level (hubs, quizzes, level articles).
  return trackLevelIntent(inferred, {
    source: extra.source || 'page_view',
    path: pathname,
    slug: extra.slug,
    persist: true,
  });
}

/** Hub / mock-picker path for a level. */
export function levelHubPath(level) {
  const id = normalizePreferredLevel(level) || 'l1';
  if (id === 'l2') return '/level/2';
  if (id === 'l3') return '/level/3';
  return '/';
}

export function levelMocksPath(level) {
  const id = normalizePreferredLevel(level) || 'l1';
  if (id === 'l2') return '/quiz/l2';
  if (id === 'l3') return '/quiz/l3';
  return '/quiz/session';
}

export const LEVEL_SWITCHER_OPTIONS = [
  { id: 'l1', label: 'Level 1', short: 'L1', href: '/', mocksHref: '/quiz/session' },
  { id: 'l2', label: 'Level 2', short: 'L2', href: '/level/2', mocksHref: '/quiz/l2' },
  { id: 'l3', label: 'Level 3', short: 'L3', href: '/level/3', mocksHref: '/quiz/l3' },
];
